import type { StartPaymentInput } from "../types/payment";
import type { StartPayResponse, StartPayStatus } from "../types/response";
import { StartPayError } from "./errors";

type NormalizeOptions = {
  status: StartPayStatus;
  input?: Pick<StartPaymentInput, "amount" | "currency" | "orderId" | "metadata">;
  rawResponse?: unknown;
  message?: string;
  errorCode?: string;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const readString = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value) return value;
  }
  return undefined;
};

export const normalizeResponse = ({ status, input, rawResponse, message, errorCode }: NormalizeOptions): StartPayResponse => {
  const raw = asRecord(rawResponse);
  const orderId = input?.orderId ?? readString(raw, ["orderId", "order_id"]) ?? "";
  const paymentId = readString(raw, ["paymentId", "payment_id"]) ?? null;
  const transactionId = readString(raw, ["transactionId", "transaction_id", "txnId"]) ?? null;
  const amount = typeof raw.amount === "number" ? raw.amount : input?.amount;
  const currency = typeof raw.currency === "string" ? raw.currency : input?.currency;
  const gateway = typeof raw.gateway === "string" ? raw.gateway : "custom";
  const metadata = (asRecord(raw.metadata) ?? input?.metadata) as Record<string, unknown>;
  const timestamp = new Date().toISOString();

  if (status === "success") {
    return {
      status,
      success: true,
      paymentId: paymentId ?? "",
      orderId,
      transactionId,
      amount,
      currency,
      gateway,
      message: message ?? "Payment completed successfully",
      rawResponse,
      metadata,
      timestamp
    };
  }

  if (status === "cancelled") {
    return {
      status,
      success: false,
      paymentId: null,
      orderId,
      transactionId: null,
      amount,
      currency,
      gateway,
      errorCode: "PAYMENT_CANCELLED",
      message: message ?? "Payment cancelled by user",
      rawResponse,
      metadata,
      timestamp
    };
  }

  if (status === "error") {
    return {
      status,
      success: false,
      orderId,
      errorCode: errorCode ?? "SDK_ERROR",
      message: message ?? "Something went wrong while starting payment",
      details: rawResponse,
      timestamp
    };
  }

  if (status === "pending") {
    return {
      status,
      success: false,
      paymentId,
      orderId,
      transactionId,
      amount,
      currency,
      gateway,
      message: message ?? "Payment is pending",
      rawResponse,
      metadata,
      timestamp
    };
  }

  return {
    status: "failed",
    success: false,
    paymentId,
    orderId,
    transactionId,
    amount,
    currency,
    gateway,
    errorCode: errorCode ?? "PAYMENT_FAILED",
    message: message ?? "Payment failed",
    rawResponse,
    metadata,
    timestamp
  };
};

export const normalizeError = (error: unknown, orderId?: string): StartPayResponse => {
  const startPayError = error instanceof StartPayError ? error : new StartPayError("Something went wrong while starting payment", "SDK_ERROR", error);
  return {
    status: "error",
    success: false,
    orderId,
    errorCode: startPayError.code,
    message: startPayError.message,
    details: startPayError.details,
    timestamp: new Date().toISOString()
  };
};
